import { Modal, Typography } from 'antd';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { AIS_LAUNCH_PATH } from './ais/aisSignin.ts';
import { useAppStore } from './hooks/store/useAppStore';
import { useCurrentContextStore } from './hooks/store/useCurrentContextStore.ts';
import { patientFullName } from './utils/patient.ts';

export const PatientImportModal: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    patientData,
    setPatientData,
    setDiagnose,
    setAcute,
    setLabInfo,
    setElgaOptOut,
    setAdditionalData,
    setAdditionalDataInvalidFields,
    reset,
    tempAisImportResult,
    setTempAisImportResult,
    setAisImportResult,
    setAisLaunchIdForCreate,
  } = useAppStore(
    useShallow(state => ({
      reset: state.reset,
      patientData: state.patientData,
      setPatientData: state.setPatientData,
      setDiagnose: state.setDiagnose,
      setAcute: state.setAcute,
      setLabInfo: state.setLabInfo,
      setElgaOptOut: state.setElgaOptOut,
      setAdditionalData: state.setAdditionalData,
      setAdditionalDataInvalidFields: state.setAdditionalDataInvalidFields,
      tempAisImportResult: state.tempAisImportResult,
      setTempAisImportResult: state.setTempAisImportResult,
      setAisImportResult: state.setAisImportResult,
      setAisLaunchIdForCreate: state.setAisLaunchIdForCreate,
    }))
  );

  const { setCurrentDoctorId } = useCurrentContextStore();

  // Back must not return to the launch URL, whose redeem would wipe the running request
  const replace = pathname === AIS_LAUNCH_PATH;

  const setStateAndNavigate = useCallback(() => {
    if (!tempAisImportResult) {
      return;
    }

    // under the lock the user cannot complete what labcomplete could not take over; the AIS owns it
    const imported = tempAisImportResult.patientDataFromAis ? { ...tempAisImportResult, invalidPatientFields: [] } : tempAisImportResult;

    reset();
    setAisImportResult(imported);
    setAisLaunchIdForCreate(tempAisImportResult.aisLaunchId ?? undefined);
    setPatientData(tempAisImportResult.patientData);
    setDiagnose(tempAisImportResult.diagnose);
    setAcute(tempAisImportResult.acute ?? false);
    setLabInfo(tempAisImportResult.labInfo ?? '');
    setElgaOptOut(tempAisImportResult.elgaOptOut ?? false);
    setAdditionalData(tempAisImportResult.additionalData);
    setAdditionalDataInvalidFields(tempAisImportResult.invalidAdditionalDataFields);

    if (tempAisImportResult.switchToDoctorId) {
      setCurrentDoctorId(tempAisImportResult.switchToDoctorId);
    }

    if (imported.invalidPatientFields.length > 0) {
      navigate('/anforderung/patient-erfassung', {
        state: { invalidFields: imported.invalidPatientFields, validateInitial: true },
        replace,
      });
    } else {
      navigate('/anforderung/parameterauswahl', { replace });
    }
    setTempAisImportResult(null);
  }, [
    tempAisImportResult,
    reset,
    setAisImportResult,
    setAisLaunchIdForCreate,
    setPatientData,
    setDiagnose,
    setAcute,
    setLabInfo,
    setElgaOptOut,
    setAdditionalData,
    setAdditionalDataInvalidFields,
    setTempAisImportResult,
    setCurrentDoctorId,
    navigate,
    replace,
  ]);

  useEffect(() => {
    if (!patientData && tempAisImportResult) {
      setStateAndNavigate();
    }
  }, [patientData, tempAisImportResult, setStateAndNavigate]);

  return (
    <Modal
      title="Neuer Patient geladen"
      open={!!patientData && !!tempAisImportResult}
      onCancel={() => setTempAisImportResult(null)}
      onOk={() => setStateAndNavigate()}
      okText="Ja"
      cancelText="Nein"
    >
      Wollen Sie die aktuelle Anforderung für <Typography.Text strong>{patientData && patientFullName(patientData)}</Typography.Text>{' '}
      abbrechen und eine neue Anforderung für{' '}
      <Typography.Text strong>{tempAisImportResult && patientFullName(tempAisImportResult.patientData)}</Typography.Text> starten?
    </Modal>
  );
};
